import { useState, useEffect, useMemo, useCallback } from 'react';
import { X, Search, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { Select } from '../../../components/ui/Select';
import { DriverBookingsCalendarPicker } from './DriverBookingsCalendarPicker';

import type {
  BookingRecord,
  DriverRecord,
} from '../../../types';

import {
  formatBookingDate,
  formatBookingTime,
  formatEuro,
} from '../../../utils/format';

type DriverBookingsModalProps = {
  driver: DriverRecord | null;
  bookings: BookingRecord[];
  isOpen: boolean;
  isLoading?: boolean;
  onClose: () => void;
};

const getBookingDay = (iso: string): string => {
  if (!iso) return '';
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(iso.trim());
  if (match) return match[1];
  const d = new Date(iso);
  if (!Number.isNaN(d.getTime())) {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  return '';
};

export const getDriverBookingIncome = (booking: BookingRecord): number => {
  const driverAmount = Number(booking.driverAmount);
  if (!Number.isNaN(driverAmount) && driverAmount > 0) {
    return driverAmount;
  }
  // When driver is assigned, fallback to the trip fare
  if (booking.driverAssigned) {
    const amount = Number(booking.amountEur || booking.finalPrice || 0);
    if (!Number.isNaN(amount) && amount > 0) {
      return amount;
    }
  }
  return 0;
};

export const DriverBookingsModal = ({
  driver,
  bookings,
  isOpen,
  isLoading = false,
  onClose,
}: DriverBookingsModalProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [paymentFilter, setPaymentFilter] = useState<
    '' | 'paid' | 'unpaid'
  >('');

  // Reset filters when modal opens/closes or driver changes
  useEffect(() => {
    setSearchQuery('');
    setStartDate('');
    setEndDate('');
    setPaymentFilter('');
  }, [isOpen, driver?.id]);

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const hasActiveFilters = Boolean(
    normalizedQuery || startDate || endDate || paymentFilter
  );

  const resetAllFilters = () => {
    setSearchQuery('');
    setStartDate('');
    setEndDate('');
    setPaymentFilter('');
  };

  const filteredBookings = useMemo(() => {
    return bookings.filter((booking) => {
      // Payment status filter
      if (
        paymentFilter &&
        booking.payment !== paymentFilter
      ) {
        return false;
      }
      // 1. Date Range Filter
      if (startDate || endDate) {
        const bookingDay = getBookingDay(booking.scheduledAt);
        if (bookingDay) {
          if (startDate && bookingDay < startDate) return false;
          if (endDate && bookingDay > endDate) return false;
        }
      }

      // 2. Text Query Filter
      if (!normalizedQuery) return true;

      const customerName = booking.customer?.name?.toLowerCase() || '';
      const customerEmail = booking.customer?.email?.toLowerCase() || '';
      const customerPhone = (
        booking.customerPhone ||
        (booking.customer as { phone?: string })?.phone ||
        ''
      ).toLowerCase();
      const reference = (booking.reference || '').toLowerCase();
      const service = (booking.service || '').toLowerCase();
      const vehicle = (booking.vehicle || '').toLowerCase();
      const from = (booking.route?.from || '').toLowerCase();
      const to = (booking.route?.to || '').toLowerCase();
      const scheduledAt = (booking.scheduledAt || '').toLowerCase();
      const dateFormatted = booking.scheduledAt
        ? formatBookingDate(booking.scheduledAt).toLowerCase()
        : '';
      const timeFormatted = booking.scheduledAt
        ? formatBookingTime(booking.scheduledAt).toLowerCase()
        : '';
      const amount = String(booking.amountEur || '');
      const driverIncome = String(getDriverBookingIncome(booking));
      const payment = (booking.payment || '').toLowerCase();
      const trip = (booking.trip || '').toLowerCase();
      const status = (booking.status || '').toLowerCase();

      return (
        customerName.includes(normalizedQuery) ||
        customerEmail.includes(normalizedQuery) ||
        customerPhone.includes(normalizedQuery) ||
        reference.includes(normalizedQuery) ||
        service.includes(normalizedQuery) ||
        vehicle.includes(normalizedQuery) ||
        from.includes(normalizedQuery) ||
        to.includes(normalizedQuery) ||
        scheduledAt.includes(normalizedQuery) ||
        dateFormatted.includes(normalizedQuery) ||
        timeFormatted.includes(normalizedQuery) ||
        amount.includes(normalizedQuery) ||
        driverIncome.includes(normalizedQuery) ||
        payment.includes(normalizedQuery) ||
        trip.includes(normalizedQuery) ||
        status.includes(normalizedQuery)
      );
    });
    }, [
      bookings,
      normalizedQuery,
      startDate,
      endDate,
      paymentFilter,
    ]);

  // Total assigned income calculated from this driver's bookings
  const totalAssignedIncome = useMemo(() => {
    return bookings.reduce((sum, b) => sum + getDriverBookingIncome(b), 0);
  }, [bookings]);

  // Overall driver income (profile income or calculated booking income)
  const driverTotalIncome = useMemo(() => {
    const profileIncome = Number(driver?.incomeEur) || 0;
    return Math.max(profileIncome, totalAssignedIncome);
  }, [driver?.incomeEur, totalAssignedIncome]);

  // Filtered assigned income from currently visible bookings
  const filteredAssignedIncome = useMemo(() => {
    return filteredBookings.reduce(
      (sum, b) => sum + getDriverBookingIncome(b),
      0
    );
  }, [filteredBookings]);

  // Total trip fare for visible/filtered bookings
  const filteredTotalFare = useMemo(() => {
    return filteredBookings.reduce(
      (sum, b) => sum + Number(b.amountEur || b.finalPrice || 0),
      0
    );
  }, [filteredBookings]);

  // Overall trip fare across all assigned bookings for this driver
  const allBookingsTotalFare = useMemo(() => {
    return bookings.reduce(
      (sum, b) => sum + Number(b.amountEur || b.finalPrice || 0),
      0
    );
  }, [bookings]);

  const handleExport = useCallback(() => {
    if (!filteredBookings.length || !driver) {
      toast.error('No bookings to export');
      return;
    }

    try {
      const filteredPassengers = filteredBookings.reduce(
        (sum, b) => sum + Number(b.passengers || 0),
        0
      );
      const filteredLuggage = filteredBookings.reduce(
        (sum, b) => sum + Number(b.luggage || 0),
        0
      );
      const allPassengers = bookings.reduce(
        (sum, b) => sum + Number(b.passengers || 0),
        0
      );
      const allLuggage = bookings.reduce(
        (sum, b) => sum + Number(b.luggage || 0),
        0
      );

      // 1. Individual booking rows
      const rows: Array<Record<string, string | number>> = filteredBookings.map((booking) => {
        const driverTripIncome = getDriverBookingIncome(booking);
        const isAssigned = booking.driverAssigned || driverTripIncome > 0;
        return {
          'Booking ID': booking.reference,
          'Customer': booking.customer?.name || '',
          'Phone':
            booking.customerPhone ||
            (booking.customer as { phone?: string })?.phone ||
            '',
          'Email': booking.customer?.email || '',
          'Pickup Location': booking.route?.from || '',
          'Drop-off Location': booking.route?.to || '',
          'Date': booking.scheduledAt
            ? formatBookingDate(booking.scheduledAt)
            : '',
          'Time': booking.scheduledAt
            ? formatBookingTime(booking.scheduledAt)
            : '',
          'Service': booking.service || '',
          'Vehicle': booking.vehicle || '',
          'Driver Name': driver.name,
          'Driver Code': driver.code,
          'Trip Fare (EUR)': Number(Number(booking.amountEur || booking.finalPrice || 0).toFixed(2)),
          'Driver Booking Income (EUR)': Number(driverTripIncome.toFixed(2)),
          'Company Share (EUR)': Number(
            (
              Number(booking.amountEur || booking.finalPrice || 0) -
              driverTripIncome
            ).toFixed(2)
          ),
          'Total Driver Income (EUR)': Number(driverTotalIncome.toFixed(2)),
          'Income Status': isAssigned ? 'Assigned' : 'Unassigned',
          'Payment Status': booking.payment || '',
          'Trip Status': booking.trip || '',
          'Passengers': booking.passengers ?? '',
          'Luggage': booking.luggage ?? '',
        };
      });

      // 2. Empty spacer row
      rows.push({
        'Booking ID': '',
        'Customer': '',
        'Phone': '',
        'Email': '',
        'Pickup Location': '',
        'Drop-off Location': '',
        'Date': '',
        'Time': '',
        'Service': '',
        'Vehicle': '',
        'Driver Name': '',
        'Driver Code': '',
        'Trip Fare (EUR)': '',
        'Driver Booking Income (EUR)': '',
        'Company Share (EUR)': '',
        'Total Driver Income (EUR)': '',
        'Income Status': '',
        'Payment Status': '',
        'Trip Status': '',
        'Passengers': '',
        'Luggage': '',
      });

      // 3. Filtered summary row
      const filterDateLabel =
        startDate && endDate
          ? `${startDate} to ${endDate}`
          : startDate
          ? `From ${startDate}`
          : endDate
          ? `Until ${endDate}`
          : 'All Bookings';

      rows.push({
        'Booking ID': hasActiveFilters ? 'TOTAL (FILTERED)' : 'TOTAL',
        'Customer': `${filteredBookings.length} booking${
          filteredBookings.length === 1 ? '' : 's'
        }`,
        'Phone': '',
        'Email': '',
        'Pickup Location': '',
        'Drop-off Location': '',
        'Date': filterDateLabel,
        'Time': '',
        'Service': '',
        'Vehicle': '',
        'Driver Name': driver.name,
        'Driver Code': driver.code,
        'Trip Fare (EUR)': Number(filteredTotalFare.toFixed(2)),
        'Driver Booking Income (EUR)': Number(filteredAssignedIncome.toFixed(2)),
        'Company Share (EUR)': Number(
          (filteredTotalFare - filteredAssignedIncome).toFixed(2)
        ),
        'Total Driver Income (EUR)': Number(driverTotalIncome.toFixed(2)),
        'Income Status': `${filteredBookings.length} Bookings`,
        'Payment Status': '',
        'Trip Status': '',
        'Passengers': filteredPassengers,
        'Luggage': filteredLuggage,
      });

      // 4. Overall total driver income summary row when active filters are applied
      if (hasActiveFilters) {
        rows.push({
          'Booking ID': 'TOTAL DRIVER INCOME (OVERALL)',
          'Customer': `All ${bookings.length} assigned booking${
            bookings.length === 1 ? '' : 's'
          }`,
          'Phone': driver.phone || '',
          'Email': driver.email || '',
          'Pickup Location': '',
          'Drop-off Location': '',
          'Date': 'All-Time Total',
          'Time': '',
          'Service': '',
          'Vehicle': '',
          'Driver Name': driver.name,
          'Driver Code': driver.code,
          'Trip Fare (EUR)': Number(allBookingsTotalFare.toFixed(2)),
          'Driver Booking Income (EUR)': Number(totalAssignedIncome.toFixed(2)),
          'Company Share (EUR)': Number(
            (allBookingsTotalFare - totalAssignedIncome).toFixed(2)
          ),
          'Total Driver Income (EUR)': Number(driverTotalIncome.toFixed(2)),
          'Income Status': 'Total Driver Income',
          'Payment Status': '',
          'Trip Status': '',
          'Passengers': allPassengers,
          'Luggage': allLuggage,
        });
      }

      const worksheet = XLSX.utils.json_to_sheet(rows);

      worksheet['!cols'] = [
        { wch: 30 }, // Booking ID
        { wch: 28 }, // Customer
        { wch: 18 }, // Phone
        { wch: 26 }, // Email
        { wch: 28 }, // Pickup Location
        { wch: 28 }, // Drop-off Location
        { wch: 22 }, // Date
        { wch: 12 }, // Time
        { wch: 18 }, // Service
        { wch: 18 }, // Vehicle
        { wch: 20 }, // Driver Name
        { wch: 14 }, // Driver Code
        { wch: 16 }, // Trip Fare (EUR)
        { wch: 24 }, // Driver Booking Income (EUR)
        { wch: 24 }, // Company Share (EUR)
        { wch: 24 }, // Total Driver Income (EUR)
        { wch: 20 }, // Income Status
        { wch: 16 }, // Payment Status
        { wch: 14 }, // Trip Status
        { wch: 12 }, // Passengers
        { wch: 10 }, // Luggage
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Driver Bookings');

      // 5. Secondary 'Driver Summary' tab for clean executive overview
      const summaryRows = [
        { 'Summary Item': 'Driver Name', 'Details': driver.name },
        { 'Summary Item': 'Driver Code', 'Details': driver.code },
        { 'Summary Item': 'Phone', 'Details': driver.phone || 'N/A' },
        { 'Summary Item': 'Email', 'Details': driver.email || 'N/A' },
        {
          'Summary Item': 'Total Driver Income (EUR)',
          'Details': Number(driverTotalIncome.toFixed(2)),
        },
        {
          'Summary Item': 'Total Assigned Bookings Fare (EUR)',
          'Details': Number(allBookingsTotalFare.toFixed(2)),
        },
        {
          'Summary Item': 'Total Assigned Bookings Income (EUR)',
          'Details': Number(totalAssignedIncome.toFixed(2)),
        },
        {
          'Summary Item': 'Total Assigned Bookings Count',
          'Details': bookings.length,
        },
        {
          'Summary Item': 'Exported Bookings Count',
          'Details': filteredBookings.length,
        },
        {
          'Summary Item': 'Exported Bookings Total Fare (EUR)',
          'Details': Number(filteredTotalFare.toFixed(2)),
        },
        {
          'Summary Item': 'Exported Bookings Driver Income (EUR)',
          'Details': Number(filteredAssignedIncome.toFixed(2)),
        },
                {
          'Summary Item': 'Exported Bookings Company Share (EUR)',
          'Details': Number(
            (filteredTotalFare - filteredAssignedIncome).toFixed(2)
          ),
        },
        {
          'Summary Item': 'Overall Company Share (EUR)',
          'Details': Number(
            (allBookingsTotalFare - totalAssignedIncome).toFixed(2)
          ),
        },
        {
          'Summary Item': 'Date Range Filter',
          'Details': filterDateLabel,
        },
        {
          'Summary Item': 'Export Date & Time',
          'Details': new Date().toLocaleString(),
        },
      ];

      const summaryWorksheet = XLSX.utils.json_to_sheet(summaryRows);
      summaryWorksheet['!cols'] = [{ wch: 38 }, { wch: 32 }];
      XLSX.utils.book_append_sheet(workbook, summaryWorksheet, 'Driver Summary');

      const today = new Date().toISOString().split('T')[0];
      const sanitizedCode = (driver.code || 'driver')
        .toLowerCase()
        .replace(/[^a-z0-9_-]/g, '-');
      const filename = `${sanitizedCode}-bookings-${today}.xlsx`;

      XLSX.writeFile(workbook, filename);
      toast.success(
        `Exported ${filteredBookings.length} booking${
          filteredBookings.length === 1 ? '' : 's'
        } with total income to Excel`
      );
    } catch (err) {
      console.error('Failed to export driver bookings to Excel', err);
      toast.error('Failed to export bookings');
    }
  }, [
    filteredBookings,
    bookings,
    driver,
    driverTotalIncome,
    totalAssignedIncome,
    filteredAssignedIncome,
    filteredTotalFare,
    allBookingsTotalFare,
    hasActiveFilters,
    startDate,
    endDate,
  ]);

  if (!isOpen || !driver) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex max-h-[88vh] w-full max-w-[1080px] flex-col overflow-hidden rounded-[20px] bg-white shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-4 border-b border-[#EEEEEE] px-6 py-4">
          <div>
            <h2 className="font-host text-[20px] font-bold text-black">
              Bookings
            </h2>
            <p className="mt-0.5 font-host text-[13px] text-[#757575]">
              {driver.name} • {driver.code}
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Driver Income & Assignment Summary Card */}
            <div className="flex items-center gap-3.5 rounded-[14px] bg-[#F7F9F5] border border-[#E2EBD6] px-4 py-2 shadow-2xs">
              <div className="flex flex-col text-right">
                <span className="font-host text-[10px] font-bold uppercase tracking-wider text-[#5A7A32]">
                  Total Driver Income
                </span>
                <span className="font-host text-[18px] font-black text-[#719642] tabular-nums">
                  {formatEuro(driverTotalIncome)}
                </span>
              </div>
              <div className="h-7 w-px bg-[#D6E4C4]" />
              <div className="flex flex-col text-right">
                <span className="font-host text-[10px] font-bold uppercase tracking-wider text-[#757575]">
                  Assigned Fare
                </span>
                <span className="font-host text-[16px] font-bold text-black tabular-nums">
                  {formatEuro(totalAssignedIncome)}
                </span>
              </div>
              <div className="h-7 w-px bg-[#D6E4C4]" />
              <div className="flex flex-col text-left">
                <span className="font-host text-[10px] font-medium text-[#757575] uppercase">
                  Bookings
                </span>
                <span className="font-host text-[15px] font-bold text-black tabular-nums">
                  {bookings.length}
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="flex h-[34px] w-[34px] items-center justify-center rounded-full bg-[#1A1A1A] text-white transition-colors hover:bg-[#333333] cursor-pointer"
              aria-label="Close modal"
            >
              <X size={17} />
            </button>
          </div>
        </div>

        {/* Search, Date Filter & Export Toolbar */}
        <div className="relative z-30 flex flex-wrap items-center justify-between gap-3 border-b border-[#EEEEEE] bg-[#FAFAFA] px-6 py-3">
          <div className="flex flex-wrap items-center gap-2.5 flex-1 min-w-[280px]">
            {/* Search Input */}
            <div className="relative min-w-[180px] flex-1 max-w-[300px]">
              <Search
                size={15}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A8A] pointer-events-none"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search customer, ID, route..."
                className="h-[38px] w-full rounded-full border border-[#E0E0E0] bg-white pl-8 pr-8 font-host text-[13px] text-black placeholder:text-[#8A8A8A] focus:border-[#FF6A1A] focus:outline-none transition-colors"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#8A8A8A] hover:text-black transition-colors cursor-pointer"
                  title="Clear search"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Interactive Visual Calendar Date Range Picker */}
            <DriverBookingsCalendarPicker
              startDate={startDate}
              endDate={endDate}
              onChange={(start, end) => {
                setStartDate(start);
                setEndDate(end);
              }}
              initialFocusDate={bookings[0]?.scheduledAt}
            /> 

            {/* Custom payment status dropdown */}
            <div
              className={`
                w-[180px] shrink-0
                [&>div>button]:!rounded-full
                [&>div>button]:!border-[#E0E0E0]
                [&>div>button]:!px-3
                [&>div>button]:!gap-2
                [&>div>button>span]:!text-[13px]
                [&>div>button>span]:!font-normal
                [&>div>button>img]:!size-4
                [&_ul_button]:!text-[13px]
                [&_ul_button]:!font-normal
                ${
                  paymentFilter
                    ? '[&>div>button>span]:!text-black'
                    : '[&>div>button>span]:!text-[#8A8A8A]'
                }
              `}
            >
              <Select<'' | 'paid' | 'unpaid'>
                label="Payment Status"
                value={paymentFilter}
                options={[
                  { value: 'paid', label: 'Paid' },
                  { value: 'unpaid', label: 'Unpaid' },
                ]}
                onChange={(value) => setPaymentFilter(value)}
                height={38}
                panelGap={6}
              />
            </div>
      
            {/* Active Filter Indicators / Reset */}
            {hasActiveFilters && (
              <button
                type="button"
                onClick={resetAllFilters}
                className="font-host text-[12px] font-semibold text-[#FF6A1A] hover:underline cursor-pointer"
              >
                Reset filters
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            {hasActiveFilters && (
              <span className="font-host text-[12px] font-medium text-[#757575] bg-white border border-[#E0E0E0] px-3 py-1.5 rounded-full">
                {filteredBookings.length} of {bookings.length} found
              </span>
            )}

            <button
              type="button"
              onClick={handleExport}
              disabled={filteredBookings.length === 0}
              className="flex h-[38px] items-center gap-2 rounded-full border border-[#E0E0E0] bg-white px-4 font-host text-[13px] font-semibold text-black transition-all hover:bg-[#F0F0F0] active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed shadow-xs cursor-pointer"
              title={
                filteredBookings.length === 0
                  ? 'No bookings to export'
                  : `Export ${filteredBookings.length} booking${
                      filteredBookings.length === 1 ? '' : 's'
                    } to Excel`
              }
            >
              <Download size={15} className="text-[#FF6A1A]" />
              <span>Export Results</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="min-h-0 flex-1 overflow-auto p-6">
          {isLoading ? (
            <div className="flex min-h-[220px] items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#E0E0E0] border-t-[#FF6A1A]" />
            </div>
          ) : bookings.length === 0 ? (
            <div className="flex min-h-[220px] items-center justify-center rounded-[12px] bg-[#FAFAFA]">
              <p className="font-host text-[15px] text-[#757575]">
                No bookings assigned to this driver.
              </p>
            </div>
          ) : filteredBookings.length === 0 ? (
            <div className="flex min-h-[220px] flex-col items-center justify-center gap-2 rounded-[12px] bg-[#FAFAFA] p-6 text-center">
              <Search size={32} className="text-[#BDBDBD] mb-1" />
              <p className="font-host text-[15px] font-medium text-black">
                No bookings match your search / date criteria
              </p>
              <p className="font-host text-[13px] text-[#757575]">
                Try adjusting your date range or search keyword.
              </p>
              <button
                type="button"
                onClick={resetAllFilters}
                className="mt-2 text-[13px] font-semibold text-[#FF6A1A] hover:underline cursor-pointer"
              >
                Clear all filters
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[920px] border-collapse">
                <thead>
                  <tr className="border-b border-[#EEEEEE]">
                    <th className="px-4 py-4 text-left font-host text-[14px] font-medium text-[#8A8A8A]">
                      Customer
                    </th>

                    <th className="px-4 py-4 text-left font-host text-[14px] font-medium text-[#8A8A8A]">
                      Booking ID
                    </th>

                    <th className="px-4 py-4 text-left font-host text-[14px] font-medium text-[#8A8A8A]">
                      Type
                    </th>

                    <th className="px-4 py-4 text-left font-host text-[14px] font-medium text-[#8A8A8A]">
                      Date & Time
                    </th>

                    <th className="px-4 py-4 text-left font-host text-[14px] font-medium text-[#8A8A8A]">
                      Vehicle
                    </th>

                    <th className="px-4 py-4 text-right font-host text-[14px] font-medium text-[#8A8A8A]">
                      Total Fare
                    </th>

                    <th className="px-4 py-4 text-right font-host text-[14px] font-medium text-[#8A8A8A]">
                      Driver Income
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {filteredBookings.map((booking) => {
                    const driverIncome = getDriverBookingIncome(booking);
                    const isIncomeAssigned =
                      driverIncome > 0 || booking.driverAssigned;

                    return (
                      <tr
                        key={booking.id}
                        className="border-b border-[#F3F3F3] last:border-b-0 hover:bg-[#FAFAFA] transition-colors"
                      >
                        {/* Customer */}
                        <td className="px-4 py-5">
                          <div className="flex flex-col">
                            <span className="font-host text-[15px] font-medium text-black">
                              {booking.customer.name}
                            </span>
                            {booking.route?.from && booking.route?.to && (
                              <span className="font-host text-[12px] text-[#757575] truncate max-w-[220px]">
                                {booking.route.from} → {booking.route.to}
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Booking ID */}
                        <td className="px-4 py-5">
                          <span
                            className="block max-w-[120px] truncate font-host text-[13px] font-mono text-black"
                            title={booking.reference}
                          >
                            {booking.reference}
                          </span>
                        </td>

                        {/* Type */}
                        <td className="px-4 py-5">
                          <span className="font-host text-[14px] font-medium text-black">
                            {booking.service}
                          </span>
                        </td>

                        {/* Date & Time */}
                        <td className="px-4 py-5">
                          <div className="flex flex-col gap-1">
                            <span className="whitespace-nowrap font-host text-[14px] font-medium text-black">
                              {formatBookingDate(booking.scheduledAt)}
                            </span>

                            <span className="font-host text-[14px] text-[#8A8A8A]">
                              {formatBookingTime(booking.scheduledAt)}
                            </span>
                          </div>
                        </td>

                        {/* Vehicle */}
                        <td className="px-4 py-5">
                          <span className="font-host text-[14px] font-medium text-black">
                            {booking.vehicle}
                          </span>
                        </td>

                        {/* Total Fare */}
                        <td className="px-4 py-5 text-right">
                          <span className="font-host text-[15px] font-semibold text-black tabular-nums">
                            {formatEuro(booking.amountEur)}
                          </span>
                        </td>

                        {/* Driver Income */}
                        <td className="px-4 py-5 text-right">
                          <div className="flex flex-col items-end">
                            <span className="font-host text-[16px] font-black text-[#719642] tabular-nums">
                              {formatEuro(driverIncome)}
                            </span>
                            <span
                              className={`inline-flex items-center gap-1 font-host text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full border mt-0.5 ${
                                isIncomeAssigned
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                  : 'bg-gray-50 text-gray-500 border-gray-200'
                              }`}
                            >
                              {isIncomeAssigned ? 'Assigned' : 'Pending'}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-[#EEEEEE] px-6 py-4">
          <div className="flex flex-wrap items-center gap-3 text-[13px] font-host">
            <span className="text-[#757575]">
              {filteredBookings.length === bookings.length
                ? `Total: ${bookings.length} booking${
                    bookings.length === 1 ? '' : 's'
                  }`
              : `Showing ${filteredBookings.length} of ${
                  bookings.length
                } booking${bookings.length === 1 ? '' : 's'}`}
            </span>
            <span className="text-gray-300">•</span>
            <span className="text-black font-medium">
              Filtered Driver Income:{' '}
              <span className="text-[#719642] font-bold tabular-nums">
                {formatEuro(filteredAssignedIncome)}
              </span>
            </span>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="h-[44px] min-w-[120px] rounded-[22px] border border-[#E0E0E0] bg-white px-6 font-host text-[14px] font-semibold text-black transition-colors hover:bg-[#F8F8F8] cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};